import { z } from 'zod'

import { UserDisplayFlagType } from 'apps/dashboard/constants/DisplayFlagType'
import { PageStatus } from 'apps/profile/models/Page/helpers'
import { AccountStatus } from 'common/core/models/Account/constants'
import { UserRoles } from 'common/userRoles'

import { InvalidEmailErrorSchema } from '../billing/errors'

const AccountTagAnalyticsSchema = z.object({
  account: z.object({
    id: z.number(),
    name: z.string(),
    ava_big: z.string().nullish(),
  }),
  actual_role: z
    .object({
      id: z.number(),
      title: z.string(),
    })
    .optional(),
  stats: z.object({
    content_send: z.number().nullable(),
    content_send_without_tag: z.number().nullable(),
    content_send_other_without_tag: z.number().nullable(),
    content_send_otn_without_token: z.number().nullable(),
    flows_count: z.number().nullable(),
  }),
})

const FetchMessageTagAnalyticsResponseSchema = z.object({
  accounts: z.array(AccountTagAnalyticsSchema),
  last_available_date: z.string().nullable(),
  analytics_date_from: z.string().nullable(),
})

import {
  CommonEmailErrorSchema,
  EmailUsedErrorSchema,
  ExpiredCodeErrorSchema,
  IncorrectCodeErrorSchema,
  LimitExceededErrorSchema,
  VerifiedErrorSchema,
} from './errors'

const AccountSchema = z.object({
  avatar_url: z.string().nullish(),
  is_active_fb_administrator: z.boolean().optional(),
  is_favorite: z.boolean(),
  is_hidden: z.boolean(),
  mac_total: z.number().optional(),
  page_id: z.string(),
  page_name: z.string().optional(),
  pro_status_name: z.nativeEnum(AccountStatus).optional(),
  status_name: z.nativeEnum(PageStatus).optional(),
  subscribers_active: z.number().nullish(),
  subscribers_total: z.number().nullish(),
  threads_open: z.number().nullish(),
  ts_last_seen: z.string().nullish(),
  user_role_name: z.nativeEnum(UserRoles).optional(),
  stat_slicing: z
    .object({
      date: z.string(),
      subscribers_total: z.number(),
      subscribers_active: z.number(),
    })
    .nullish(),
})

export enum ReminderType {
  REGION_ACCESSIBILITY = 'region_accessibility',
  CGT_FEATURE = 'cgt_feature',
  REF_URL_FEATURE = 'ref_url_feature',
}

export const ProfileApiSchemas = {
  setDisplayFlag: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      display_flag: z.nativeEnum(UserDisplayFlagType),
      value: z.boolean(),
    }),
    response: z.undefined(),
  },
  fetchInactiveAccounts: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      account_ids: z.array(z.string()),
    }),
  },
  fetchPages: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      fb: z.object({
        accounts: z.array(AccountSchema),
      }),
    }),
  },
  sendEmailVerification: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      email: z.string(),
    }),
    response: z.object({
      email: z.string().nullable(),
      email_unverified: z.string().nullable(),
    }),
    errors: {
      commonEmailError: CommonEmailErrorSchema,
      emailUsedError: EmailUsedErrorSchema,
      emailInvalidError: InvalidEmailErrorSchema,
      limitExceededError: LimitExceededErrorSchema,
    },
  },
  verifyEmailViaCode: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      code: z.string(),
    }),
    response: z.object({
      email: z.string().nullable(),
      email_unverified: z.string().nullable(),
    }),
    errors: {
      limitExceededError: LimitExceededErrorSchema,
      emailVerifiedError: VerifiedErrorSchema,
      expiredCodeError: ExpiredCodeErrorSchema,
      incorrectCodeError: IncorrectCodeErrorSchema,
    },
  },
  clearRegSourceParam: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.undefined(),
  },
  addToWaitlistTikTokCreatorType: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      full_name: z.string(),
      email: z.string(),
      reason: z.string(),
    }),
    response: z.undefined(),
    errors: {
      emailError: CommonEmailErrorSchema,
    },
  },
  addToTikTokReminderListForUpdates: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      reminder_type: z.nativeEnum(ReminderType),
    }),
    response: z.undefined(),
  },
  setSkipPreviewOnboardingModal: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      skip_preview_onboarding_modal: z.boolean(),
    }),
    response: z.undefined(),
  },
  confirmEmail: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      code: z.string(),
    }),
    response: z.undefined(),
  },
  setTimezone: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      timezone: z.string(),
    }),
    response: z.object({
      timezone: z.string(),
    }),
  },
  fetchMessageTagAnalytics: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: FetchMessageTagAnalyticsResponseSchema,
  },
  fetchAllAccounts: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      accounts: z.array(AccountSchema),
    }),
  },
  fetchAccount: {
    path: z.undefined(),
    query: z.object({
      account_id: z.string().or(z.number()),
    }),
    request: z.undefined(),
    response: z.object({
      account: AccountSchema,
    }),
  },
}

export type AccountTagAnalytics = z.infer<typeof AccountTagAnalyticsSchema>
export type FetchMessageTagAnalyticsResponse = z.infer<
  typeof FetchMessageTagAnalyticsResponseSchema
>
export type Account = z.infer<typeof AccountSchema>
